import type { AiFeatureErrorResponse } from './atAGlanceTypes.ts';
import {
	type AtAGlanceRetryReason,
	getServiceRetryReason,
	getRetryAfterDelay,
} from './atAGlanceRetry.ts';

const isAiFeatureErrorResponse = (value: unknown): value is AiFeatureErrorResponse => {
	if (!value || typeof value !== 'object') {
		return false;
	}

	const error = (value as { error?: unknown }).error;
	if (!error || typeof error !== 'object') {
		return false;
	}

	const candidate = error as Record<string, unknown>;
	return (
		typeof candidate.code === 'string' &&
		typeof candidate.message === 'string' &&
		typeof candidate.retryable === 'boolean' &&
		typeof candidate.requestId === 'string'
	);
};

/**
 * Retains the cc-smarts error envelope for diagnostics and retry classification while
 * keeping the surfaced Error message generic. Recognized transient failures support one delayed
 * automatic retry after the server cooldown.
 */
export class AtAGlanceRequestError extends Error {
	readonly status: number;
	readonly code: string | undefined;
	readonly retryable: boolean | undefined;
	readonly requestId: string | undefined;
	readonly serviceMessage: string | undefined;
	readonly retryAfterMs: number;
	readonly retryReason: AtAGlanceRetryReason | undefined;

	constructor(status: number, responseBody: unknown, retryAfter: string | null = null) {
		super(`At a Glance request failed with HTTP ${status}`);
		this.name = 'AtAGlanceRequestError';
		this.status = status;

		if (isAiFeatureErrorResponse(responseBody)) {
			this.code = responseBody.error.code;
			this.retryable = responseBody.error.retryable;
			this.requestId = responseBody.error.requestId;
			this.serviceMessage = responseBody.error.message;
		}
		this.retryReason = getServiceRetryReason(status, this.code, this.retryable);
		this.retryAfterMs = getRetryAfterDelay(
			retryAfter,
			this.retryReason === 'transientServiceError' ? 2_000 : undefined,
		);
	}
}
